import React, { useMemo, useCallback, type KeyboardEvent, type MouseEvent } from 'react';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import {
	useForgeApps,
	useIsAppLoading,
} from '@atlassian/jira-forge-action-apps-controller/src/index.tsx';
import { ForgeScreenEvent } from '@atlassian/jira-forge-ui-analytics/src/common/ui/index.tsx';
import { getAnalyticsAttributesFromExtension } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-analytics-attributes-from-extension/index.tsx';
import { fireUiTriggerClickedEvent } from '@atlassian/jira-forge-issue-navigator-action/src/analytics/services/index.tsx';
import { TriggerAnalyticsWrapper } from '@atlassian/jira-forge-issue-navigator-action/src/analytics/ui/index.tsx';
import { ExtensionTitle } from '@atlassian/jira-forge-ui-extension-title/src/ui/index.tsx';
import type { ForgeUiIssueNavigatorData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type { ActionsProps } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { useSelectedIssueKey } from '@atlassian/jira-issue-navigator/src/controllers/selected-issue/facade.tsx';
import {
	useActiveJql,
	useResolvedJql,
} from '@atlassian/jira-issue-navigator/src/services/active-jql/index.tsx';
import type { ForgePluggableEntity } from '../../../services/query/index.tsx';
import { ACTIONS_MIN_WIDTH } from '../../constants.tsx';
import { AppIcon } from '../app-icon/index.tsx';

type ForgeDropdownItemProps = {
	item: ForgePluggableEntity;
	totalCount: number;
};

export const ForgeDropdownItemWithErrorBoundary = (props: ForgeDropdownItemProps) => {
	return (
		<ErrorBoundary packageName="software" id="forge-dropdown-item">
			<ForgeDropdownItem {...props} />
		</ErrorBoundary>
	);
};

export function ForgeDropdownItem({ item, totalCount }: ForgeDropdownItemProps) {
	const jql = useResolvedJql();
	const { filterId } = useActiveJql();
	const issueKey = useSelectedIssueKey();

	const issueNavigatorData = useMemo(
		() => ({
			jql,
			filterId,
			issueKeys: issueKey ? [issueKey] : [],
		}),
		[jql, filterId, issueKey],
	);

	const title = useMemo(() => {
		return (
			<ExtensionTitle
				extensionName={item.title}
				extensionEnvType={item.metadata.environmentType}
				extensionEnvKey={item.metadata.environmentKey}
				autoTruncate
			/>
		);
	}, [item.metadata.environmentKey, item.metadata.environmentType, item.title]);

	return item.metadata.properties.actions ? (
		<ForgeDropdownItemWithActions
			item={item}
			totalCount={totalCount}
			issueNavigatorData={issueNavigatorData}
			title={title}
		/>
	) : (
		<ForgeDropdownItemSimple
			item={item}
			totalCount={totalCount}
			issueNavigatorData={issueNavigatorData}
			title={title}
		/>
	);
}

function ForgeDropdownItemSimple({
	item,
	totalCount,
	issueNavigatorData,
	title,
}: {
	item: ForgePluggableEntity;
	totalCount: number;
	issueNavigatorData: Omit<ForgeUiIssueNavigatorData, 'action'>;
	title: React.ReactNode;
}) {
	return (
		<Box>
			<DropdownItemWithAnalytics
				key={item.id}
				itemType="forge-app"
				testId="issue-navigator-action-pluggable-items.ui.dropdown-item.forge-dropdown-item.action-drop-down-item"
				item={item}
				totalCount={totalCount}
				issueNavigatorData={issueNavigatorData}
				tooltip={typeof item.tooltip === 'string' ? item.tooltip : undefined}
				title={title}
			/>
		</Box>
	);
}

function ForgeDropdownItemWithActions({
	item,
	totalCount,
	issueNavigatorData,
	title,
}: {
	item: ForgePluggableEntity;
	totalCount: number;
	issueNavigatorData: Omit<ForgeUiIssueNavigatorData, 'action'>;
	title: React.ReactNode;
}) {
	return (
		<DropdownMenu
			key={item.id}
			placement="right-start"
			shouldFlip
			spacing="compact"
			trigger={({ triggerRef, ...triggerProps }) => (
				<DropdownItem
					{...triggerProps}
					ref={triggerRef}
					title={typeof item.tooltip === 'string' ? item.tooltip : undefined}
					elemBefore={<AppIcon iconUrl={item.iconUrl} />}
					elemAfter={
						<ChevronRightIcon spacing="spacious" color={token('color.icon.subtle')} label="" />
					}
				>
					{title}
				</DropdownItem>
			)}
			shouldRenderToParent
		>
			<Box xcss={actionsContainerStyles}>
				<DropdownItemGroup>
					{(item.metadata.properties.actions ?? []).map((action) => {
						return (
							<DropdownItemWithAnalytics
								key={`${item.id}${action.key}`}
								itemType="forge-action"
								testId="issue-navigator-action-pluggable-items.ui.dropdown-item.forge-dropdown-item.action-drop-down-item"
								item={item}
								action={action}
								totalCount={totalCount}
								issueNavigatorData={issueNavigatorData}
								tooltip={typeof action.tooltip === 'string' ? action.tooltip : undefined}
								title={action.title}
							/>
						);
					})}
				</DropdownItemGroup>
			</Box>
		</DropdownMenu>
	);
}

type DropdownItemWithAnalyticsProps = {
	totalCount: number;
	testId: string;
	item: ForgePluggableEntity;
	title: string | React.ReactNode;
	action?: ActionsProps;
	itemType: string;
	issueNavigatorData: ForgeUiIssueNavigatorData;
	tooltip?: string;
};

export function DropdownItemWithAnalytics(props: DropdownItemWithAnalyticsProps) {
	const { totalCount, itemType, item, action } = props;
	const analyticsAttributes = useMemo(
		() => ({
			...getAnalyticsAttributesFromExtension(item.metadata),
			analyticsAppName: item.title,
			analyticsActionName: action?.title,
			analyticsAppCount: totalCount,
			analyticsAppType: itemType,
			source: 'issue-navigator',
		}),
		[action?.title, item.metadata, item.title, itemType, totalCount],
	);

	const handleOnClick = useCallback(() => {
		fireUiTriggerClickedEvent(item.id, 'issue-navigator', analyticsAttributes);
	}, [analyticsAttributes, item.id]);

	return (
		<TriggerAnalyticsWrapper attributes={analyticsAttributes}>
			<DropdownItemInner {...props} onClick={handleOnClick} />
			<ForgeScreenEvent attributes={analyticsAttributes} />
		</TriggerAnalyticsWrapper>
	);
}

function DropdownItemInner({
	totalCount,
	itemType,
	testId,
	item,
	title,
	action,
	issueNavigatorData,
	onClick,
	tooltip,
	...props
}: DropdownItemWithAnalyticsProps & { onClick: () => void }) {
	const [isAppLoading] = useIsAppLoading({ appId: item.id, action: action?.key });
	const [, { openApp }] = useForgeApps();

	const handleOnClick = useCallback(
		(event: KeyboardEvent<Element> | MouseEvent<Element>) => {
			event.stopPropagation();
			onClick();
			openApp(item.id, item.metadata, action?.key, { ...issueNavigatorData, action: action?.key });
		},
		[action?.key, issueNavigatorData, item.id, item.metadata, onClick, openApp],
	);

	return (
		<DropdownItem
			{...props}
			title={tooltip}
			onClick={handleOnClick}
			testId={testId}
			elemAfter={<LoadingIndicator isAppLoading={isAppLoading} />}
			elemBefore={<AppIcon iconUrl={item.iconUrl} />}
		>
			{title}
		</DropdownItem>
	);
}

function LoadingIndicator({ isAppLoading }: { isAppLoading: boolean }) {
	return <Box xcss={indicatorStyle}>{isAppLoading && <Spinner size="small" />}</Box>;
}

const indicatorStyle = xcss({
	width: token('space.150'),
});

const actionsContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${ACTIONS_MIN_WIDTH}px`,
});
